import { DrawPathTool } from "@treegram/tools-draw-path";
import { useCallback, useEffect, useRef } from "react";

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";

import { Area, Line, type MeasureData } from "@treegram/viewer-core";
import { DrawLineTool } from "@treegram/tools-draw-line";
import { DrawPolygonTool } from "@treegram/tools-draw-polygon";
import { CameraTool } from "@treegram/tools-camera";
import { HighlighterTool } from "@treegram/tools-highlighter";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useStoreyMeasuresContext from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measures/use-storey-measures-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";

import {
    ModelingMeasureRepresentation,
    ModelingType,
} from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import Measure from "@/pages/takeoff/context/project-measure/models/measure.ts";
import GeometryRepresentation from "@/pages/takeoff/context/project-measure/models/geometry-representation.ts";
import GeometryRepresentationOperation from "@/models/enums/geometry-representation-operation.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

export interface DrawingProps {
    measureTypeId?: string;
    drawingColor: THREE.Color;
    drawingAlpha: number;
    drawingMeasureRepresentation: ModelingMeasureRepresentation;
    drawingDataInformations?: Record<string, unknown>;
}

const useStoreyMeasuresDrawingTool = () => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { getTool } = useViewerContext();
    const { getMeasureType } = useProjectMeasureContext();
    const { measuresRef, addMeasure, handleGeometriesSelected } = useStoreyMeasuresContext();
    const { addItems } = useViewerItemsVisibilityContext();
    const { setIsInDrawingMode, isInDrawingMode } = useModelingToolsContext();

    const tempIds = useRef<Map<string, string[]>>(new Map<string, string[]>());
    const currentDrawingTypeRef = useRef<ModelingType>(ModelingType.Point);
    const currentDrawingPropsRef = useRef<DrawingProps | undefined>(undefined);

    const setTempId = useCallback(
        (type: ModelingType, measureRepresentation: ModelingMeasureRepresentation, id: string) => {
            const key = `${type}:${measureRepresentation}`;
            let ids = tempIds.current.get(key);
            if (ids == null) {
                ids = [];
                tempIds.current.set(key, ids);
            }

            ids.push(id);
        },
        [],
    );

    const createGeometry = useCallback(
        (id: string) =>
            new GeometryRepresentation(id, GeometryRepresentationOperation.Addition, crypto.randomUUID()),
        [],
    );

    const handleAddMeasure = useCallback(
        (dataInformations: Record<string, unknown> | undefined) => {
            if (!ensureCanEdit()) {
                return;
            }

            const measureTypeId = currentDrawingPropsRef.current?.measureTypeId;
            if (!measureTypeId) return;

            const measuresOfThisType = measuresRef.current.filter((m) => m.measureTypeId === measureTypeId);
            const quantity = measuresOfThisType.length;
            const name = getMeasureType(measureTypeId)?.name ?? "";
            const measure = new Measure(
                measureTypeId,
                `${name} - ${quantity + 1}`,
                crypto.randomUUID(),
                dataInformations,
            );
            const drawingType = currentDrawingTypeRef.current;
            const drawingMeasureRepresentation =
                currentDrawingPropsRef.current?.drawingMeasureRepresentation ??
                ModelingMeasureRepresentation.LineImprint;
            const key = `${drawingType}:${drawingMeasureRepresentation}`;
            const geometriesIds = tempIds.current.get(key);
            if (geometriesIds != null) {
                addItems(geometriesIds);
                const geometries = geometriesIds.map(createGeometry);
                measure.setGeometryRepresentations(geometries);
            }

            addMeasure(measure);
            tempIds.current.set(key, []);
        },
        [ensureCanEdit, measuresRef, addMeasure, createGeometry, getMeasureType, addItems],
    );

    const lineDrawingEndedCallback = useCallback(
        async (e: Line) => {
            if (getTool(DrawLineTool) == null) return;

            const measureRep =
                currentDrawingPropsRef.current?.drawingMeasureRepresentation ??
                ModelingMeasureRepresentation.LineImprint;
            setTempId(ModelingType.Line, measureRep, e.id);
            handleAddMeasure(e.dataInformations);
        },
        [getTool, handleAddMeasure, setTempId],
    );

    const surfaceDrawingEndedCallback = useCallback(
        async (e: Area) => {
            if (getTool(DrawPolygonTool) == null) return;

            const measureRep =
                currentDrawingPropsRef.current?.drawingMeasureRepresentation ??
                ModelingMeasureRepresentation.LineImprint;
            setTempId(ModelingType.Surface, measureRep, e.id);
            handleAddMeasure(e.dataInformations);
        },
        [getTool, handleAddMeasure, setTempId],
    );

    const pathDrawingEndedCallback = useCallback(
        (measure: MeasureData) => {
            const props = currentDrawingPropsRef.current;
            if (!props) return;
            setTempId(currentDrawingTypeRef.current, props.drawingMeasureRepresentation, measure.id);
            handleAddMeasure(measure.dataInformations);
        },
        [handleAddMeasure, setTempId],
    );

    const startDrawing = useCallback(
        async (drawingType: ModelingType, isSnappingEnabled: boolean, drawingProps: DrawingProps) => {
            if (!ensureCanEdit()) {
                return;
            }

            currentDrawingTypeRef.current = drawingType;
            currentDrawingPropsRef.current = drawingProps;

            const lineTool = getTool(DrawLineTool);
            const polygonTool = getTool(DrawPolygonTool);
            const cameraTool = getTool(CameraTool);
            const highlighter = getTool(HighlighterTool);

            if (cameraTool != null) {
                cameraTool.setNavigationMode("Plan");
            }

            if (highlighter != null) {
                await highlighter.deactivate();
            }

            const snappings = isSnappingEnabled
                ? [FRAGS.SnappingClass.LINE, FRAGS.SnappingClass.POINT, FRAGS.SnappingClass.FACE]
                : [];

            switch (drawingType) {
                case ModelingType.Point:
                case ModelingType.Polyline: {
                    const pathTool = getTool(DrawPathTool);
                    if (!pathTool) return;
                    pathTool.configure(
                        drawingType === ModelingType.Point ? "Point" : "Polyline",
                        drawingProps.drawingColor,
                        drawingProps.drawingAlpha,
                        drawingProps.drawingDataInformations ?? {},
                        snappings,
                    );
                    pathTool.onEndedCreation.add(pathDrawingEndedCallback);
                    await pathTool.activate();
                    break;
                }
                case ModelingType.Line: {
                    if (lineTool == null) return;
                    await lineTool.activate();

                    lineTool.setColor(drawingProps.drawingColor);
                    lineTool.setAlpha(drawingProps.drawingAlpha);
                    lineTool.setMeasureRepresentation(drawingProps.drawingMeasureRepresentation);
                    lineTool.setSnappings(snappings);
                    if (drawingProps.drawingDataInformations)
                        lineTool.setDataInformations(drawingProps.drawingDataInformations);
                    lineTool.draw();
                    lineTool.onEndedCreation.add(lineDrawingEndedCallback);
                    break;
                }

                case ModelingType.Surface: {
                    if (polygonTool == null) return;
                    await polygonTool.activate();

                    polygonTool.setColor(drawingProps.drawingColor);
                    polygonTool.setAlpha(drawingProps.drawingAlpha);
                    polygonTool.setMeasureRepresentation(drawingProps.drawingMeasureRepresentation);
                    polygonTool.setSnappings(snappings);
                    if (drawingProps.drawingDataInformations)
                        polygonTool.setDataInformations(drawingProps.drawingDataInformations);
                    polygonTool.draw();
                    polygonTool.onEndedCreation.add(surfaceDrawingEndedCallback);
                    break;
                }
                default:
                    break;
            }

            setIsInDrawingMode(true);
        },
        [
            ensureCanEdit,
            getTool,
            lineDrawingEndedCallback,
            pathDrawingEndedCallback,
            setIsInDrawingMode,
            surfaceDrawingEndedCallback,
        ],
    );

    const stopDrawing = useCallback(
        async (clear: boolean) => {
            const lineTool = getTool(DrawLineTool);
            const polygonTool = getTool(DrawPolygonTool);
            const cameraTool = getTool(CameraTool);
            const highlighter = getTool(HighlighterTool);

            if (cameraTool != null) {
                cameraTool.setNavigationMode("Orbit");
            }

            if (highlighter != null) {
                await highlighter.activate();
                highlighter.onItemsSelected.add(handleGeometriesSelected);
            }

            const pathTool = getTool(DrawPathTool);
            pathTool?.onEndedCreation.remove(pathDrawingEndedCallback);
            if (clear) pathTool?.clear();
            await pathTool?.deactivate();
            lineTool?.onEndedCreation.remove(lineDrawingEndedCallback);
            polygonTool?.onEndedCreation.remove(surfaceDrawingEndedCallback);

            if (clear) {
                lineTool?.clear();
                polygonTool?.clear();
            }

            await lineTool?.deactivate();
            await polygonTool?.deactivate();

            setIsInDrawingMode(false);
        },
        [
            getTool,
            handleGeometriesSelected,
            lineDrawingEndedCallback,
            pathDrawingEndedCallback,
            setIsInDrawingMode,
            surfaceDrawingEndedCallback,
        ],
    );

    useEffect(() => {
        if (canEdit || !isInDrawingMode) return;
        stopDrawing(true).catch((error: unknown) => {
            console.error("Impossible de suspendre l’outil de modification.", error);
        });
    }, [canEdit, isInDrawingMode, stopDrawing]);

    return {
        startDrawing,
        stopDrawing,
    };
};

export default useStoreyMeasuresDrawingTool;
